import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { getEnvironment, isRecognizedMode, type Environment } from '@/utils/environmentUtils'
import { useDashboardDeploymentsStore } from '@/stores/dashboard-deployments.store'
import {
  DEFAULT_DASHBOARD_TARGET,
  isDashboardTargetAllowed,
  isDashboardTargetSupported as isTargetSupportedIn,
  parseDashboardTarget
} from '@/utils/dashboardTarget'
import type {
  EnvironmentColorOption,
  ColorOption,
  DashboardTarget,
  UiSettings
} from '@/types/ui-settings.types'

export const UI_SETTINGS_STORAGE_KEY = 'adx-ui-settings'
export const DASHBOARD_TARGET_STORAGE_KEY = 'adx-dashboard-target'

/**
 * Violet, which the whole theme turns while a dashboard target is in effect, so the admin can
 * see from any page that Login as merchant is not going to the normal dashboard.
 */
export const DASHBOARD_TARGET_COLOR_OPTION: ColorOption = {
  value: 'purple',
  label: 'Violet',
  cssVar: '156, 39, 176'
}

// Traditional 12-color wheel (RYB)
export const colorOptions: ColorOption[] = [
  { value: 'red', label: 'Red', cssVar: '255, 82, 82' },
  { value: 'redOrange', label: 'Red-Orange', cssVar: '255, 87, 34' },
  { value: 'orange', label: 'Orange', cssVar: '251, 140, 0' },
  { value: 'amber', label: 'Amber', cssVar: '244, 167, 33' },
  { value: 'yellow', label: 'Yellow', cssVar: '255, 235, 59' },
  { value: 'lime', label: 'Yellow-Green', cssVar: '205, 220, 57' },
  { value: 'green', label: 'Green', cssVar: '38, 194, 85' },
  { value: 'teal', label: 'Blue-Green', cssVar: '0, 150, 136' },
  { value: 'blue', label: 'Blue', cssVar: '0, 113, 232' },
  { value: 'indigo', label: 'Navy', cssVar: '31, 58, 116' },
  DASHBOARD_TARGET_COLOR_OPTION,
  { value: 'redViolet', label: 'Red-Violet', cssVar: '199, 21, 133' }
]

export const UI_SETTINGS_VERSION = 2

// Dev and staging open with the theme on in their own colour (green, amber), so an admin can tell at
// a glance which environment they are in; production keeps the plain look.
const isNonProductionMode = (): boolean => isRecognizedMode() && getEnvironment() !== 'PROD'

// Test merchants are shown by default in every environment: production has only a few, and admins look
// popular ones like Hot Pizza up by name. The Active status filter already keeps stale ones out of the list.
const buildDefaultSettings = (): UiSettings => ({
  settingsVersion: UI_SETTINGS_VERSION,
  showEnvironmentBorder: isNonProductionMode(),
  devColor: 'green',
  stagingColor: 'amber',
  prodColor: 'indigo',
  dashboardRows: 3,
  recentlyJoinedMonths: 1,
  impersonationDuration: 15,
  hideTestAdmins: true,
  hideTestPartners: true,
  hideTestPartnerIntegrators: true,
  hideTestMerchants: false
})

/**
 * Settings saved before version 2 hold the old defaults (theme off, every colour Navy, test merchants
 * hidden in production), because every visit saves the full set. A value still equal to the old
 * default was never chosen, so it moves to the new default once; anything the admin changed is kept.
 */
const migrateToCurrentDefaults = (parsed: Partial<UiSettings>): Partial<UiSettings> => {
  if ((parsed.settingsVersion ?? 1) >= UI_SETTINGS_VERSION) return parsed

  const defaults = buildDefaultSettings()
  return {
    ...parsed,
    settingsVersion: UI_SETTINGS_VERSION,
    showEnvironmentBorder:
      parsed.showEnvironmentBorder === false || parsed.showEnvironmentBorder === undefined
        ? defaults.showEnvironmentBorder
        : parsed.showEnvironmentBorder,
    devColor: !parsed.devColor || parsed.devColor === 'indigo' ? defaults.devColor : parsed.devColor,
    stagingColor:
      !parsed.stagingColor || parsed.stagingColor === 'indigo' ? defaults.stagingColor : parsed.stagingColor,
    ...(parsed.hideTestMerchants && (!isRecognizedMode() || getEnvironment() === 'PROD')
      ? { hideTestMerchants: defaults.hideTestMerchants }
      : {})
  }
}

/**
 * Reads the persisted settings, filling gaps from the defaults. The dashboard target lasts
 * only for the browser tab, so a target or Local Dev flag left in here by an older build is
 * dropped rather than restored.
 */
export const loadPersistedUiSettings = (): UiSettings => {
  try {
    const stored = localStorage.getItem(UI_SETTINGS_STORAGE_KEY)
    if (stored) {
      const {
        useLocalDev: _useLocalDev,
        dashboardTarget: _dashboardTarget,
        ...parsed
      } = JSON.parse(stored) as Partial<UiSettings> & {
        useLocalDev?: boolean
        dashboardTarget?: unknown
      }
      return { ...buildDefaultSettings(), ...migrateToCurrentDefaults(parsed) }
    }
  } catch (e) {
    console.warn('Failed to load UI settings from localStorage:', e)
  }
  return { ...buildDefaultSettings() }
}

/**
 * The dashboard target chosen in this browser tab. A new tab, or a tab whose storage cannot be
 * read, starts on Default.
 */
export const loadTabDashboardTarget = (): DashboardTarget => {
  try {
    const stored = sessionStorage.getItem(DASHBOARD_TARGET_STORAGE_KEY)
    return stored ? parseDashboardTarget(JSON.parse(stored)) : DEFAULT_DASHBOARD_TARGET
  } catch {
    return DEFAULT_DASHBOARD_TARGET
  }
}

const saveTabDashboardTarget = (target: DashboardTarget) => {
  try {
    if (target.kind === 'default') {
      sessionStorage.removeItem(DASHBOARD_TARGET_STORAGE_KEY)
    } else {
      sessionStorage.setItem(DASHBOARD_TARGET_STORAGE_KEY, JSON.stringify(target))
    }
  } catch (e) {
    console.warn('Failed to save the dashboard target to sessionStorage:', e)
  }
}

/**
 * Store for UI display settings that persist to localStorage
 */
export const useUiSettingsStore = defineStore('adx-ui-settings', () => {
  const saveSettings = (settings: UiSettings) => {
    try {
      localStorage.setItem(UI_SETTINGS_STORAGE_KEY, JSON.stringify(settings))
    } catch (e) {
      console.warn('Failed to save UI settings to localStorage:', e)
    }
  }

  // === State ===
  const settings = ref<UiSettings>(loadPersistedUiSettings())
  // Persist straight away so a target or Local Dev flag from an older build leaves localStorage.
  saveSettings(settings.value)
  const tabDashboardTarget = ref<DashboardTarget>(loadTabDashboardTarget())

  // === Getters ===
  const showEnvironmentBorder = computed({
    get: () => settings.value.showEnvironmentBorder,
    set: (value: boolean) => {
      settings.value.showEnvironmentBorder = value
      saveSettings(settings.value)
    }
  })

  const devColor = computed({
    get: () => settings.value.devColor,
    set: (value: EnvironmentColorOption) => {
      settings.value.devColor = value
      saveSettings(settings.value)
    }
  })

  const stagingColor = computed({
    get: () => settings.value.stagingColor,
    set: (value: EnvironmentColorOption) => {
      settings.value.stagingColor = value
      saveSettings(settings.value)
    }
  })

  const prodColor = computed({
    get: () => settings.value.prodColor,
    set: (value: EnvironmentColorOption) => {
      settings.value.prodColor = value
      saveSettings(settings.value)
    }
  })

  const dashboardRows = computed({
    get: () => settings.value.dashboardRows,
    set: (value: number) => {
      // Clamp between 1 and 10 rows
      settings.value.dashboardRows = Math.max(1, Math.min(10, value))
      saveSettings(settings.value)
    }
  })

  const recentlyJoinedMonths = computed({
    get: () => settings.value.recentlyJoinedMonths,
    set: (value: number) => {
      // Clamp between 1 and 3 months
      settings.value.recentlyJoinedMonths = Math.max(1, Math.min(3, value))
      saveSettings(settings.value)
    }
  })

  const impersonationDuration = computed({
    get: () => settings.value.impersonationDuration,
    set: (value: number) => {
      settings.value.impersonationDuration = value
      saveSettings(settings.value)
    }
  })

  const hideTestAdmins = computed({
    get: () => settings.value.hideTestAdmins,
    set: (value: boolean) => {
      settings.value.hideTestAdmins = value
      saveSettings(settings.value)
    }
  })

  const hideTestPartners = computed({
    get: () => settings.value.hideTestPartners,
    set: (value: boolean) => {
      settings.value.hideTestPartners = value
      saveSettings(settings.value)
    }
  })

  const hideTestPartnerIntegrators = computed({
    get: () => settings.value.hideTestPartnerIntegrators,
    set: (value: boolean) => {
      settings.value.hideTestPartnerIntegrators = value
      saveSettings(settings.value)
    }
  })

  const hideTestMerchants = computed({
    get: () => settings.value.hideTestMerchants,
    set: (value: boolean) => {
      settings.value.hideTestMerchants = value
      saveSettings(settings.value)
    }
  })

  const currentEnvironment = computed<Environment>(() => getEnvironment())

  // An unrecognized build mode reads as DEV, so require a known mode before offering targets.
  const isDashboardTargetSupported = computed(
    () => isRecognizedMode() && isTargetSupportedIn(currentEnvironment.value)
  )

  const dashboardTarget = computed<DashboardTarget>(() => tabDashboardTarget.value)

  /** The target in force here: a saved target this environment cannot use counts as Default. */
  const activeDashboardTarget = computed<DashboardTarget>(() =>
    isDashboardTargetSupported.value &&
    isDashboardTargetAllowed(tabDashboardTarget.value, currentEnvironment.value)
      ? tabDashboardTarget.value
      : DEFAULT_DASHBOARD_TARGET
  )

  /**
   * The target Login as merchant really goes to in this tab, by name, or null on the default
   * dashboard. A deployment counts only while the deployment list still offers it.
   */
  const dashboardTargetLabel = computed<string | null>(() => {
    const target = activeDashboardTarget.value
    if (target.kind === 'local') return 'Local dev'
    if (target.kind === 'deployment' && useDashboardDeploymentsStore().isTargetUsable(target)) {
      return target.branch
    }
    return null
  })

  const isDashboardTargeted = computed(() => dashboardTargetLabel.value !== null)

  /** The colour the admin chose for this environment. */
  const savedEnvColor = computed<EnvironmentColorOption>(() => {
    switch (currentEnvironment.value) {
      case 'DEV':
        return settings.value.devColor
      case 'STAGING':
        return settings.value.stagingColor
      default:
        return settings.value.prodColor
    }
  })

  /**
   * The colour the theme shows: Violet while a dashboard target is in effect, otherwise the
   * admin's own choice. The saved choice is never changed, so Default or a new tab restores it.
   */
  const activeEnvColor = computed<EnvironmentColorOption>(() =>
    isDashboardTargeted.value ? DASHBOARD_TARGET_COLOR_OPTION.value : savedEnvColor.value
  )

  const activeEnvColorOption = computed(() =>
    colorOptions.find(c => c.value === activeEnvColor.value)
  )

  /**
   * Whether the theme colours ADX: the admin's saved Theme switch, or a dashboard target, which
   * always shows Violet. The saved switch is never changed, so Default restores the admin's look.
   */
  const isThemeApplied = computed(
    () => settings.value.showEnvironmentBorder || isDashboardTargeted.value
  )

  const environmentBorderColor = computed(() => {
    if (!isThemeApplied.value) return null
    const color = activeEnvColorOption.value
    return color ? `rgb(${color.cssVar})` : null
  })

  /**
   * Get the CSS variable string for the current environment color
   */
  const currentEnvColorCss = computed(() => activeEnvColorOption.value?.cssVar ?? '31, 58, 116')

  // === Actions ===
  const setDashboardTarget = (target: DashboardTarget) => {
    const parsed = parseDashboardTarget(target)
    tabDashboardTarget.value = isDashboardTargetAllowed(parsed, currentEnvironment.value)
      ? parsed
      : DEFAULT_DASHBOARD_TARGET
    saveTabDashboardTarget(tabDashboardTarget.value)
  }

  const resetDashboardTarget = () => {
    setDashboardTarget(DEFAULT_DASHBOARD_TARGET)
  }

  const toggleEnvironmentBorder = () => {
    showEnvironmentBorder.value = !showEnvironmentBorder.value
  }

  const resetSettings = () => {
    settings.value = { ...buildDefaultSettings() }
    saveSettings(settings.value)
    resetDashboardTarget()
  }

  // Default Tattle logo SVG (navy blue) - used for reset to avoid caching issues
  const defaultFaviconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1963 1963">
    <circle cx="981.5" cy="981.5" r="981" fill="#1f3a74"/>
    <g>
      <path fill="none" d="M907.52,279.03v118.56c53.14.1,104.74,10.57,153.37,31.14,47.15,19.94,89.48,48.47,125.8,84.8,36.33,36.33,64.86,78.65,84.8,125.8,16.43,38.85,26.4,79.59,29.8,121.49h103.1c-13.71-264.84-230-476.08-496.88-481.79h0Z"/>
      <path fill="none" d="M907.52,487.21v273.61h303.77c-16.26-153.55-145.99-273.23-303.77-273.61h0Z"/>
      <path fill="#fff" d="M1413.02,784.95l87.98,1.72c-23-327.29-236.8-574.25-595.2-593.82l1.91,90c245.74-.55,482.05,206.91,505.31,502.1h0Z"/>
      <path fill="#fff" d="M1216.46,786.67h88.29c-9.75-162.66-114.58-377.03-397.23-397.7v87.9c188.48,10.34,292.12,147.15,308.94,309.8h0Z"/>
    </g>
    <path fill="#fff" d="M880.69,1698.26c-85.09-49.5-109.14-125.4-109.14-216.15v-872.18h137.25v277.42h185.45v127.38h-183.6s-7.17,464.28,5.93,511.58c7.95,28.69,20.34,43.48,48.99,60.45,31.07,18.41,43.52,16.37,76.06,20.1,12.88,1.48,51.8,2.67,51.8,2.67v125.22c-40.92-1.29-144.29,4.74-212.73-36.51v.02Z"/>
  </svg>`

  /**
   * Reset favicon to default (navy blue Tattle logo)
   * Uses blob URL like colored favicons to avoid browser/CDN caching issues
   */
  const resetFavicon = () => {
    const oldFavicon = document.getElementById('favicon') as HTMLLinkElement
    if (oldFavicon) {
      // Revoke old blob URL to prevent memory leak
      if (oldFavicon.href.startsWith('blob:')) {
        URL.revokeObjectURL(oldFavicon.href)
      }

      // Use blob URL for default favicon too (avoids CloudFront/browser caching issues)
      const blob = new Blob([defaultFaviconSvg], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)

      // Remove and recreate the link element to force browser refresh
      const newFavicon = document.createElement('link')
      newFavicon.id = 'favicon'
      newFavicon.rel = 'icon'
      newFavicon.type = 'image/svg+xml'
      newFavicon.href = url
      oldFavicon.parentNode?.replaceChild(newFavicon, oldFavicon)
    }
  }

  /**
   * Generate and set a dynamic favicon SVG based on the current environment color
   * Uses the Tattle circular T logo with dynamic background color
   */
  const updateFavicon = () => {
    // Keep default favicon when color theme is disabled
    if (!isThemeApplied.value) {
      resetFavicon()
      return
    }

    const color = activeEnvColorOption.value
    if (!color) return

    const rgbColor = `rgb(${color.cssVar})`

    // Tattle circular T logo SVG with dynamic background color
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1963 1963">
      <circle cx="981.5" cy="981.5" r="981" fill="${rgbColor}"/>
      <g>
        <path fill="none" d="M907.52,279.03v118.56c53.14.1,104.74,10.57,153.37,31.14,47.15,19.94,89.48,48.47,125.8,84.8,36.33,36.33,64.86,78.65,84.8,125.8,16.43,38.85,26.4,79.59,29.8,121.49h103.1c-13.71-264.84-230-476.08-496.88-481.79h0Z"/>
        <path fill="none" d="M907.52,487.21v273.61h303.77c-16.26-153.55-145.99-273.23-303.77-273.61h0Z"/>
        <path fill="#fff" d="M1413.02,784.95l87.98,1.72c-23-327.29-236.8-574.25-595.2-593.82l1.91,90c245.74-.55,482.05,206.91,505.31,502.1h0Z"/>
        <path fill="#fff" d="M1216.46,786.67h88.29c-9.75-162.66-114.58-377.03-397.23-397.7v87.9c188.48,10.34,292.12,147.15,308.94,309.8h0Z"/>
      </g>
      <path fill="#fff" d="M880.69,1698.26c-85.09-49.5-109.14-125.4-109.14-216.15v-872.18h137.25v277.42h185.45v127.38h-183.6s-7.17,464.28,5.93,511.58c7.95,28.69,20.34,43.48,48.99,60.45,31.07,18.41,43.52,16.37,76.06,20.1,12.88,1.48,51.8,2.67,51.8,2.67v125.22c-40.92-1.29-144.29,4.74-212.73-36.51v.02Z"/>
    </svg>`

    const blob = new Blob([svg], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)

    const oldFavicon = document.getElementById('favicon') as HTMLLinkElement
    if (oldFavicon) {
      // Revoke old blob URL to prevent memory leak
      if (oldFavicon.href.startsWith('blob:')) {
        URL.revokeObjectURL(oldFavicon.href)
      }
      // Replace element to force browser refresh
      const newFavicon = document.createElement('link')
      newFavicon.id = 'favicon'
      newFavicon.rel = 'icon'
      newFavicon.type = 'image/svg+xml'
      newFavicon.href = url
      oldFavicon.parentNode?.replaceChild(newFavicon, oldFavicon)
    }
  }

  // Watch for color/theme changes and update favicon
  watch(
    () => [activeEnvColor.value, isThemeApplied.value],
    () => updateFavicon(),
    { immediate: true }
  )

  return {
    // State
    settings,
    // Getters
    showEnvironmentBorder,
    devColor,
    stagingColor,
    prodColor,
    dashboardRows,
    recentlyJoinedMonths,
    impersonationDuration,
    hideTestAdmins,
    hideTestPartners,
    hideTestPartnerIntegrators,
    hideTestMerchants,
    currentEnvironment,
    isDashboardTargetSupported,
    dashboardTarget,
    activeDashboardTarget,
    dashboardTargetLabel,
    isDashboardTargeted,
    savedEnvColor,
    activeEnvColor,
    isThemeApplied,
    environmentBorderColor,
    currentEnvColorCss,
    // Actions
    toggleEnvironmentBorder,
    setDashboardTarget,
    resetDashboardTarget,
    resetSettings,
    updateFavicon
  }
})
